import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core"
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { Button, Popover } from "@mui/material"
import React, { useState } from "react"

import { SETTINGS_SECTION_STYLES } from "@/components/settings/_styles"
import i18n from "@/i18n"

import { RemoveSectionDialog } from "./remove_section_dialog"
import { SectionEditor } from "./section_editor"
import type { Section } from "./types"

const STYLES = {
  ...SETTINGS_SECTION_STYLES,
  formattingLink: "text-blue-600 underline cursor-pointer bg-transparent border-0 p-0 font-inherit",
  tooltipContainer: "p-4 text-sm bg-white border border-gray-300 rounded-lg shadow-lg max-w-sm",
  tooltipTitle: "font-semibold mb-2",
  tooltipIntro: "text-gray-600 mb-3",
  tooltipList: "space-y-2",
  tooltipCode: "bg-gray-100 px-1 rounded",
  addSectionRow: "flex justify-end mt-4",
} as const

export type { Section } from "./types"

type ReservationRuleSectionsProps = {
  sections: Section[]
  onChange: (sections: Section[]) => void
  customSectionsEnabled?: boolean
}

const FormattingTooltip: React.FC = () => (
  <div className={STYLES.tooltipContainer}>
    <p className={STYLES.tooltipTitle}>{i18n.t("app.settings.venues.reservation_rules.sections.formatting_title")}</p>
    <p className={STYLES.tooltipIntro}>{i18n.t("app.settings.venues.reservation_rules.sections.formatting_intro")}</p>
    <ul className={STYLES.tooltipList}>
      <li>
        <code className={STYLES.tooltipCode}># Heading</code> —{" "}
        {i18n.t("app.settings.venues.reservation_rules.sections.formatting_heading")}
      </li>
      <li>
        <code className={STYLES.tooltipCode}>**text**</code> —{" "}
        <strong>{i18n.t("app.settings.venues.reservation_rules.sections.formatting_bold")}</strong>
      </li>
      <li>
        <code className={STYLES.tooltipCode}>*text*</code> —{" "}
        <em>{i18n.t("app.settings.venues.reservation_rules.sections.formatting_italic")}</em>
      </li>
      <li>
        <code className={STYLES.tooltipCode}>~~text~~</code> —{" "}
        <s>{i18n.t("app.settings.venues.reservation_rules.sections.formatting_strikethrough")}</s>
      </li>
      <li>
        <code className={STYLES.tooltipCode}>[text](url)</code> —{" "}
        {i18n.t("app.settings.venues.reservation_rules.sections.formatting_link")}
      </li>
      <li>
        <code className={STYLES.tooltipCode}>- item</code> —{" "}
        {i18n.t("app.settings.venues.reservation_rules.sections.formatting_bullet")}
      </li>
      <li>
        <code className={STYLES.tooltipCode}>1. item</code> —{" "}
        {i18n.t("app.settings.venues.reservation_rules.sections.formatting_numbered")}
      </li>
      <li>
        <code className={STYLES.tooltipCode}>`code`</code> —{" "}
        {i18n.t("app.settings.venues.reservation_rules.sections.formatting_code")}
      </li>
      <li>
        <code className={STYLES.tooltipCode}>&gt; quote</code> —{" "}
        {i18n.t("app.settings.venues.reservation_rules.sections.formatting_quote")}
      </li>
    </ul>
  </div>
)

type ConfirmDialogState = {
  open: boolean
  sectionIndex: number
  sectionTitle: string
}

const ReservationRuleSections: React.FC<ReservationRuleSectionsProps> = ({
  sections,
  onChange,
  customSectionsEnabled = false,
}) => {
  const [tempIdCounter, setTempIdCounter] = useState(0)
  const [formattingAnchor, setFormattingAnchor] = useState<HTMLElement | null>(null)
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
    sectionIndex: -1,
    sectionTitle: "",
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = sections.findIndex((s) => (s.id || s.tempId) === active.id)
      const newIndex = sections.findIndex((s) => (s.id || s.tempId) === over.id)
      onChange(arrayMove(sections, oldIndex, newIndex))
    }
  }

  const handleUpdate = (index: number, field: "title" | "content", value: string) => {
    const newSections = [...sections]
    newSections[index] = { ...newSections[index], [field]: value }
    onChange(newSections)
  }

  const handleDeleteClick = (index: number) => {
    const sectionTitle =
      sections[index].title || i18n.t("app.settings.venues.reservation_rules.sections.untitled_section")
    setConfirmDialog({ open: true, sectionIndex: index, sectionTitle })
  }

  const handleDeleteConfirm = () => {
    onChange(sections.filter((_, i) => i !== confirmDialog.sectionIndex))
    setConfirmDialog({ open: false, sectionIndex: -1, sectionTitle: "" })
  }

  const handleAddSection = () => {
    const newSection: Section = { tempId: `temp-${tempIdCounter}`, title: "", content: "" }
    setTempIdCounter((c) => c + 1)
    onChange([...sections, newSection])
  }

  return (
    <div className={STYLES.container} data-testid="settings-venue-rules-sections-container">
      <h2 className={STYLES.header}>{i18n.t("app.settings.venues.reservation_rules.sections.title")}</h2>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sections.map((s) => s.id || s.tempId || "")} strategy={verticalListSortingStrategy}>
          {sections.map((section, index) => (
            <SectionEditor
              key={section.id || section.tempId}
              section={section}
              index={index}
              onUpdate={handleUpdate}
              onDelete={handleDeleteClick}
              customSectionsEnabled={customSectionsEnabled}
            />
          ))}
        </SortableContext>
      </DndContext>

      <p className={STYLES.helperText}>
        {i18n.t("app.settings.venues.reservation_rules.sections.formatting_help")}{" "}
        <button type="button" className={STYLES.formattingLink} onClick={(e) => setFormattingAnchor(e.currentTarget)}>
          {i18n.t("app.settings.venues.reservation_rules.sections.formatting")}
        </button>
      </p>
      <Popover
        open={Boolean(formattingAnchor)}
        anchorEl={formattingAnchor}
        onClose={() => setFormattingAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { className: "shadow-lg" } }}
      >
        <FormattingTooltip />
      </Popover>

      {customSectionsEnabled ? (
        <div className={STYLES.addSectionRow}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddSection}
            data-testid="settings-venue-rules-sections-btn-add"
          >
            {i18n.t("app.settings.venues.reservation_rules.sections.add_section")}
          </Button>
        </div>
      ) : null}

      <RemoveSectionDialog
        open={confirmDialog.open}
        sectionTitle={confirmDialog.sectionTitle}
        onConfirm={handleDeleteConfirm}
        onClose={() => setConfirmDialog({ open: false, sectionIndex: -1, sectionTitle: "" })}
      />
    </div>
  )
}

export default ReservationRuleSections
